<!-- Copyright 2025, Common Good Learning Tools LLC -->
<template>
	<div v-if="local_item">
		<div class="k-asset-publish-status-outer">
			<div style="flex:0 1 100%">
				<div class="k-lesson-title d-flex align-center mt-1">
					<k-spacer/>
					<b>{{site_config.asset_manager.text.status}}:</b>
					<k-spacer/>
					
					<k-tooltip bottom>
						<template v-slot:activator="{ on }">
							<k-btn v-on="on" icon x-small class="mr-2" ref="button" :accessible-label="log_showing ? 'Hide publishing log' : 'Show publishing log'" @click="log_showing = !log_showing"><k-icon small>far fa-rectangle-history</k-icon></k-btn>
						</template>
						<span>View Log</span>
					</k-tooltip>
				</div>

				<div class="mt-1 mb-1 d-flex flex-wrap align-center" style="justify-content: center;">
					<div v-for="(step, index) in steps" :key="step.step + '_' + index" :class="step.step == 'spacer' ? step.class : 'k-asset-publish-step-placeholder'">
						<k-icon v-if="step.current" color="#000">fas fa-down</k-icon>
					</div>
				</div>

				<div class="d-flex flex-wrap align-center justify-center mb-4">
					<div v-for="(step, index) in steps" :key="step.step + '_' + index">
						<div v-if="step.step == 'spacer'" class="k-asset-publish-line"></div>

						<k-tooltip v-else :disabled="step.tooltip == ''" bottom>
							<template v-slot:activator="{on}">
								<div v-on="on" class="k-asset-publish-step" :class="step.class" @click="step_clicked(step.new_status)">
									<k-icon>fas {{ step.icon }}</k-icon>
									<div v-html="step.html"></div>
								</div>
							</template>
							
							<div class="text-center" v-html="step.tooltip"></div>
						</k-tooltip>
					</div>
				</div>

				<div class="d-flex flex-wrap align-center justify-center mb-4">
					<k-tooltip v-for="step_btn in step_btns" :key="step_btn.key" :disabled="step_btn.tooltip == ''" bottom>
						<template v-slot:activator="{on}">
							<k-btn
								v-show="step_btn.visible"
								v-on="on"
								class="k-tight-btn mx-2"
								:class="step_btn.class"
								:color="step_btn.btn_color + ' darken-3'"
								outlined dark
								@click="step_clicked(step_btn.new_status)"
							>
								<k-icon small class="mr-2">fas {{step_btn.icon}}</k-icon>
								{{step_btn.btn_text}}
							</k-btn>
						</template>

						<div class="text-center" v-html="step_btn.tooltip"></div>
					</k-tooltip>
				</div>

				<div v-if="step_btns[1].msg" class="d-flex mb-2">
					<k-spacer/>
					<div :class="step_btns[1].btn_color" class="k-asset-publish-msg lighten-5" v-html="step_btns[1].msg"></div>
					<k-spacer/>
				</div>

				<div class="k-asset-publish-status-card-people mb-4">
					<div class="justify-center mb-2">
						<b class="mr-2">{{asset_type_noun}} Submitter:</b>
						
						<div class="d-flex justify-center k-user-info">
							{{creator_name || 'Unknown'}}

							<k-spacer v-if="creator_email"/>
							
							<k-tooltip v-if="creator_email" bottom>
								<template v-slot:activator="{on}">
									<k-btn v-on="on" class="ml-1" accessible-label="Email submitter" icon x-small @click="mail_to"><k-icon x-small>fas fa-envelope</k-icon></k-btn>
								</template>

								Email: {{ creator_name }}
							</k-tooltip>

							<k-tooltip v-if="creator_email" bottom>
								<template v-slot:activator="{on}">
									<k-btn v-on="on" icon x-small class="ml-1" accessible-label="Copy submitter email address" @click="copy_email_to_clipboard"><k-icon x-small>fas fa-copy</k-icon></k-btn>
								</template>

								Copy email address to clipboard
							</k-tooltip>
						</div>
					</div>

					<div v-if="user_is_admin && local_item.reviewers.length" class="justify-center mb-2">
						<b class="mr-2">Reviewer{{ local_item.reviewers.length == 1 ? '' : 's' }}:</b>
						
						<div class="k-user-info">
							<div v-for="(reviewer, index) in local_item.reviewers" :key="'reviewer_' + index" class="d-flex justify-center">
								{{reviewer.assigned_user_name}}

								<k-spacer/>

								<div class="k-active-icon d-flex">
									<k-tooltip bottom>
										<template v-slot:activator="{on}">
											<k-icon v-on="on" class="ml-1" :color="reviewer.active ? 'green' : 'gray'" x-small>
												{{ reviewer.active ? 'fas fa-user-check' : 'fas fa-user'}}
											</k-icon>
										</template>

										{{ reviewer.assigned_user_name }} is {{reviewer.active ? '' : 'not'}} an active reviewer.
									</k-tooltip>
									
									<k-spacer/>
								</div>
								
								<k-tooltip bottom>
									<template v-slot:activator="{on}">
										<k-icon v-on="on" class="ml-1" :color="reviewer.approved ? 'green' : 'gray'" x-small>
											{{ reviewer.approved ? 'fas fa-comment-check' : 'fas fa-comment-xmark'}}
										</k-icon>
									</template>

									{{ reviewer.assigned_user_name }} has {{reviewer.approved ? '' : 'not'}} approved this asset.
								</k-tooltip>
							</div>
						</div>
					</div>
						
					<div v-if="user_is_admin" class="d-flex justify-center mt-2">
						<k-btn class="ml-2" color="tertiary" x-small depressed @click="manage_reviewers">
							Manage Reviewers
							<k-icon style="color: rgba(0, 0, 0, .54)" class="ml-1" x-small>fas fa-user-magnifying-glass</k-icon>
						</k-btn>

						<k-btn v-if="payments_enabled" class="ml-2" color="tertiary" x-small depressed @click="manage_payments">
							Manage Payments
							<k-icon style="color: rgba(0, 0, 0, .54)" class="ml-1" x-small>fas fa-circle-dollar</k-icon>
						</k-btn>
					</div>
				</div>

				<div v-if="reviewer_or_publisher" class="d-flex flex-wrap align-center justify-center mx-16">
					<k-text-field
						v-model="note"
						placeholder="General Review Note"
						label="General Review Note"
						maxlength="100"
						outlined
						dense
						counter
					>
						<template v-slot:append>
							<k-btn v-if="note_changed" color="primary" depressed x-small @click="save_note">
								Update Note
							</k-btn>
						</template>
					</k-text-field>
				</div>

				<!-- by using v-if here, we don't load the AssetReview component until we've retrieved the asset's review from the server -->
				<AssetReview
					v-if="asset_review_data"
					:asset_review_data="asset_review_data"
					@review_saved="review_saved"
				/>
			</div>

			<div v-if="log_showing" class="ml-2">
				<AssetLog ref="log" :log="log"/>		
			</div>
		</div>

		<AssetPaymentDialog
			v-if="managing_payments"
			:selected_asset="payment_data"
			@updated="payment_data = $event"
			@done="managing_payments = false"
		/>

		<AssetReviewerDialog
			v-if="managing_reviewers"
			:selected_asset="local_item"
			:asset_type="asset_type"
			:payment_data="payment_data"
			@updated="reviewers_updated"
			@payment_updated="payment_updated"
			@done="managing_reviewers = false"
		/>
	</div>
</template>

<script>
import { mapState } from 'vuex'
import AssetLog from './AssetLog.vue'
import AssetReview from './AssetReview.vue'
import AssetPaymentDialog from './AssetPaymentDialog.vue'
import AssetReviewerDialog from './AssetReviewerDialog.vue'

export default {
	name: 'AssetPublishStatus',

	components: {AssetLog, AssetReview, AssetPaymentDialog, AssetReviewerDialog},

	props: {
		item: {type: Object, required: true},
		publish_role_override: {type: Boolean, default: false},
		disable_submit: {type: Boolean, default: false},
	},

	data() {
		return {
			local_item: null,
			publish_roles: [],
			creator_name: '',
			creator_email: '',
			current_publish_status: {},
			asset_review_data: null,
			log: [],
			log_showing: false,
			note: '',
			managing_payments: false,
			managing_reviewers: false,
			payment_data: null,
		}
	},

	computed: {
		...mapState(['site_config', 'user_info']),

		// the asset_type for the item: lesson, activity, or resource
		asset_type() {
			if (!empty(this.item.lesson_title)) return 'lesson'
			if (this.item.type == 'sparkl') return 'activity'
			return 'resource'
		},

		user_is_admin() {
			return this.user_info.system_role == 'admin' || this.user_info.admin_rights.includes('su')
		},

		assigned_reviewer() {
			let reviewer = this.local_item.reviewers.filter(x => x.assigned_user_id == this.user_info.user_id)
			return reviewer[0]
		},

		active_reviewer() {
			return this.assigned_reviewer?.active ? this.assigned_reviewer : false
		},

		reviewer_or_publisher() {
			return this.active_reviewer || this.publish_roles.includes('publisher')
		},

		// true unless the asset has, at some point, been set to 'under_review' -- once that's happened, the author can no longer withdraw it
		never_under_review() {
			return !this.log.some(x => x.action === 'status' && x.to === 'under_review')
		},

		alignment_required() {
			return this.site_config.asset_manager.submission_requires_alignment
		},

		submit() {
			if (this.disable_submit) return {enabled: false, review: true, alignments: true, editor_open: true}

			let review = true
			let alignments = true

			if (this.site_config.asset_manager.submission_requires_review) {
				if (Array.isArray(this.asset_review_data)){
					review = false
				} else if(this.asset_review_data.sections) {
					this.asset_review_data.template.definition.forEach(section => {
						if (section.required && section.criteria && (section.asset_types && section.asset_types.includes(this.asset_type))) {
							Object.values(this.asset_review_data.sections[section.id].criteria).every(criterion => {
								if (!criterion) review = false
								return !!criterion
							})
						}
					})
				}
			}

			if (this.site_config.asset_manager.submission_requires_alignment && !this.local_item.standards.length) alignments = false

			return {enabled: review && alignments, review, alignments, editor_open: false}
		},

		asset_id() {
			return this.asset_type == 'lesson' ? this.item.lesson_id : this.item.resource_id
		},

		asset_type_noun() {
			if (this.asset_type === 'lesson') return 'Lesson'
			if (this.item.type == 'sparkl') return this.site_config.sparkl_app_name + ' Activity'
			return 'Resource'
		},

		steps() {
			return ['unsubmitted', 'spacer', 'submitted', 'spacer', 'under_review', 'spacer', 'ready_for_publication', 'spacer', 'published'].map(this.step_bind)
		},

		step_btns() {
			return ['prev', 'next'].map(this.step_btn_bind)
		},

		note_changed() {
			return this.local_item?.review_note !== this.note
		},

		payments_enabled() {
			return this.site_config.asset_manager.payment_tracking_enabled
		},
	},

	watch: {
		item(n) {
			if (n) {
				this.local_item = n
			}
		}
	},

	mounted() {
		vapp.asset_publish_status_component = this

		// retrieve the full item, along with the publish_roles for this user and the asset_review_data
		let payload = {
			user_id: this.user_info.user_id,
			asset_type: this.asset_type,
			asset_id: this.asset_id,
		}

		U.ajax('get_unpublished_assets', payload, result => {
			if (!result.status == 'ok') {
				console.error('something went wrong: ', result)
			} else if (result.assets){
				let rv = result.assets[0];

				// note that we don't (currently at least) instantiate an Asset_Review object, because we don't currently know the template at this point; we let this happen in AssetReview.vue
				this.asset_review_data = rv.asset_review_data

				this.publish_roles = rv.roles;
				/** Giving publisher rights to editors of collections in which the asset lives */
				if (this.publish_role_override && !this.publish_roles.includes('publisher')) this.publish_roles.push('publisher')
				this.creator_name = rv.creator_name;
				this.creator_email = rv.creator_email;
				this.set_current_publish_status(rv.publish_status)

				this.local_item = this.asset_type === 'lesson' ? new Lesson(rv) : new Resource(rv)

				this.note = this.local_item.review_note

				this.load_log()
			}
		})
	},

	methods: {
		// determine the parameters needed to render each "step bubble" in the user interface
		// this function, along with step_btn_bind below, effectively defines much of the business logic around the publishing workflow
		step_bind(step) {
			if (step == 'spacer') return {step, class: 'k-asset-publish-line-placeholder'}
			let key = this.current_publish_status.key
			if (empty(key) || key == 'returned_to_author') key = 'unsubmitted'

			let steps = ['unsubmitted', 'submitted', 'under_review', 'ready_for_publication', 'published']
			let ki = steps.indexOf(key)
			let si = steps.indexOf(step)

			let o = {
				step,
				class: `k-asset-publish-step-${step}`,
				// this is set to true for the current step
				current: false,
				// html and tooltip to show for the step
				html: '',
				tooltip: '',
				// if clicked, new_status.key is the new publish_status; new_status.text is what we'll inform the user
				// if new_status is 'unclickable', set_publish_status won't do anything
				new_status: {key: null, text: ''},
			}

			let where
			// if this step is where we currently are in the publish process 'queue'
			if (ki == si) {
				where = 'current'
				o.class += ' k-asset-publish-current-step'
				o.current = true
			// else if this step is earlier than where we currently are
			} else if (si < ki) {
				where = 'earlier'
				o.class += ' k-asset-publish-earlier-step'
				o.current = false
			// else if this step is later than where we currently are
			} else {
				where = 'later'
				o.class += ' k-asset-publish-later-step'
				o.current = false
			}

			switch(step) {
				case 'unsubmitted':
					// IF STEP BEING RENDERED IS 'unsubmitted'
					o.icon = 'fa-file-user'
					// if the item is currently in the 'returned_to_author' state...
					if (this.current_publish_status.key == 'returned_to_author') {
						o.html = '<div>Returned<br>to Author</div>'

						// if the user is an active reviewer or publisher, clicking this button rejects it
						if (this.reviewer_or_publisher) {
							o.tooltip = 'Reject for<br>publication'
							o.new_status = {key: 'rejected', text: 'Rejected for Publication'}
						} else {
							// I don't think we can get to here, but if we do, don't do anything if the step is clicked
							o.new_status = 'unclickable'
						}

					// else if the asset is (currently) not in the publish process at all (never submitted, withdrawn, unpublished, or rejected), the step isn't clickable at all
					// (null and the off-workflow statuses all resolve to the 'unsubmitted' key)
					} else if (this.current_publish_status.key == 'unsubmitted') {
						// unpublished/rejected assets are treated as unsubmitted, but label the step with their actual status
						o.html = this.$is_off_workflow_status(this.local_item.publish_status) ? `<div>${this.current_publish_status.text}<br> </div>` : '<div>Not<br>Submitted</div>'
						o.new_status = 'unclickable'

					// else the asset is somewhere else in the publish process, so the label for this step is 'Not Submitted', and...
					} else {
						o.html = '<div>Not<br>Submitted</div>'

						// if the user is the author and the asset hasn't yet reached under_review, clicking this button withdraws it
						if (this.publish_roles.includes('author') && this.current_publish_status.key === 'submitted' && this.never_under_review) {
							o.tooltip = 'Withdraw from<br>publication consideration'
							o.new_status = {key: null, text: 'Withdrawn for publication consideration'}
						// if the user is an active reviewer or publisher, clicking this button returns it to the author
						} else if (this.reviewer_or_publisher) {
							o.tooltip = 'Return to author<br>for revision'
							o.new_status = {key: 'returned_to_author', text: 'Returned to Author'}
						} else {
							// I don't think we can get to here, but if we do, don't do anything if the step is clicked
							o.new_status = 'unclickable'
						}
					}

					break;
				case 'submitted':
					// IF STEP BEING RENDERED IS 'submitted'
					o.icon = 'fa-file-export'
					o.html = '<div>Submitted<br> </div>'	// <br>for<br>Review

					// if this is the original author and the asset is not currently submitted,
					if (this.publish_roles.includes('author') && ['unsubmitted', 'returned_to_author'].includes(this.current_publish_status.key) && this.submit.enabled) {
						// then clicking this step should submit it
						o.tooltip = 'Submit for<br>publication'
						o.new_status = {key: 'submitted', text: 'Submitted for Publication'}
					// else the button is unclickable: there's (currently at least) no point in moving an asset from "under review" to "submitted"
					} else {
						o.new_status = 'unclickable'
					}

					break;
				case 'under_review':
					// IF STEP BEING RENDERED IS 'under_review'
					o.icon = 'fa-file-magnifying-glass'
					o.html = '<div>Under<br>Review</div>'

					// if we're currently on this step, it's unclickable
					if (where == 'current') {
						o.new_status = 'unclickable'
					// else if the user is an active reviewer or publisher, clicking this button moves it to this step
					} else if (this.reviewer_or_publisher) {
						// but message is different depending on the direction being moved
						if (where == 'later') {
							o.tooltip = 'Begin review'
							o.new_status = {key: 'under_review', text: 'Under Review'}
						} else {
							o.tooltip = 'Return for<br>further review'
							o.new_status = {key: 'under_review', text: 'Returned for Further Review'}
						}
					// else the user (presumably the author, who doesn't have reviewer/publisher rights) can't click this step
					} else {
						o.new_status = 'unclickable'
					}

					break;
				case 'ready_for_publication':
					// IF STEP BEING RENDERED IS 'ready_for_publication'
					o.icon = 'fa-file-check'
					o.html = '<div>Approved for<br>Publication</div>'

					// if we're currently on this step, it's unclickable
					if (where == 'current') {
						o.new_status = 'unclickable'
					// else if the user has reviewer or publisher rights, clicking this button moves it to this step
					} else if (this.user_is_admin || this.active_reviewer) {
						// but message is different depending on the direction being moved
						if (where == 'later') {
							o.tooltip = 'Approve for<br>publication'
							o.new_status = {key: 'ready_for_publication', text: 'Approved for Publication'}
						} else {
							o.tooltip = 'Set status to<br>“Approved for Publication”'
							o.new_status = {key: 'ready_for_publication', text: 'Approved for Publication'}
						}
					// else the user (presumably the author, who doesn't have reviewer/publisher rights) can't click this step
					} else {
						o.new_status = 'unclickable'
					}
					break;
				case 'published':
					// IF STEP BEING RENDERED IS 'published'
					o.icon = 'fa-file-certificate'
					o.html = '<div>Published<br> </div>'

					// if we're currently on this step, it's unclickable
					if (where == 'current') {
						o.new_status = 'unclickable'
					// else if the user has publisher rights, clicking this button moves it to this step
					} else if (this.publish_roles.includes('publisher')) {
						// (there is no step later than this one)
						o.tooltip = 'Publish!'
						o.new_status = {key: 'published', text: 'Published'}
					// else the user (presumably the author, who doesn't have reviewer/publisher rights) can't click this step
					} else {
						o.new_status = 'unclickable'
					}
					break;
			}

			// if new_status is unclickable, set appropriate class
			if (o.new_status == 'unclickable') {
				o.class += ' k-asset-publish-current-unclickable'
			}

			return o
		},

		// determine the parameters needed to render the "previous" and "next" buttons that appear below the "step bubbles"
		// this function, along with step_bind above, effectively defines much of the business logic around the publishing workflow
		step_btn_bind(direction) {
			let o = {
				// note that if o.visible is false, the button won't be clickable, so it doesn't matter what the other params are
				visible: true,
				tooltip: '',	// not currently used
				btn_text: '',
				btn_color: 'black',
				// new_status.key is the new publish_status; new_status.text is what we'll inform the user; 
				// if new_status.confirm is non-empty, we'll ask the user to confirm before taking the action
				new_status: {key: null, text: '', confirm: ''},
				icon: '',
				// msg to show the user; only set for next
				msg: '',
				class: '',
			}

			let key = this.current_publish_status.key
			if (empty(key) || key == 'returned_to_author') key = 'unsubmitted'

			// IF DIRECTION IS 'prev' (BACK IN THE PUBLISHING PROCESS)
			if (direction == 'prev') {
				o.key = 'prev'
				o.icon = 'fa-left'
				// IF CURRENT STEP IS 'unsubmitted'
				if (key == 'unsubmitted') {
					if (this.current_publish_status.key == 'unsubmitted') {
						o.visible = false

					} else {	// returned_to_author
						// if item has already been returned to the author, prev is 'reject' for active reviewer/publisher
						if (this.reviewer_or_publisher) {
							o.tooltip = 'Reject for<br>publication'
							o.btn_text = 'Reject for publication'
							o.btn_color = 'black'
							o.new_status = {key: 'rejected', text: 'Rejected for Publication'}
							o.new_status.confirm = `Are you sure you want to reject this ${this.asset_type_noun} for publication consideration?`
						} else {
							o.visible = false
						}
					}

				// IF CURRENT STEP IS 'submitted'
				} else if (key == 'submitted') {
					// if the user is the author and the asset has never been under review, clicking the prev button withdraws it for consideration
					if (this.publish_roles.includes('author') && this.never_under_review) {
						o.tooltip = 'Withdraw from<br>publication consideration'
						o.btn_text = 'Withdraw from publication consideration'
						o.btn_color = 'black'
						o.new_status = {key: null, text: 'Withdrawn for publication consideration'}
						o.new_status.confirm = `Are you sure you want to withdraw this ${this.asset_type_noun} for publication consideration?`
					// if the user is an active reviewer or publisher, clicking the prev button returns it to the author
					} else if (this.reviewer_or_publisher) {
						o.tooltip = 'Return to author<br>for revision'
						o.btn_text = 'Return to author for revision'
						o.btn_color = 'deep-orange'
						o.new_status = {key: 'returned_to_author', text: 'Returned to Author'}
					} else {
						// I don't think we can get to here, but if we do, button is not visible
						o.visible = false
					}

				// IF CURRENT STEP IS 'under_review'
				} else if (key == 'under_review') {
					// if the user is an active reviewer or publisher, clicking the prev button returns it to the author
					if (this.reviewer_or_publisher) {
						o.tooltip = 'Return to author<br>for revision'
						o.btn_text = 'Return to author for revision'
						o.btn_color = 'deep-orange'
						o.new_status = {key: 'returned_to_author', text: 'Returned to Author'}
					} else {
						o.visible = false
					}

				// IF CURRENT STEP IS 'ready_for_publication'
				} else if (key == 'ready_for_publication') {
					// if the user is an active reviewer or publisher, clicking the prev button returns to the reviewer
					if (this.reviewer_or_publisher) {
						o.tooltip = 'Return for<br>further review'
						o.btn_text = 'Return for further review'
						o.btn_color = 'purple'
						o.new_status = {key: 'under_review', text: 'Returned for Further Review'}
					} else {
						o.visible = false
					}

				// IF CURRENT STEP IS 'published'
				} else if (key == 'published') {
					// if the user is an active reviewer or publisher, clicking the prev button unpublishes
					if (this.reviewer_or_publisher) {
						o.tooltip = 'Unpublish'
						o.btn_text = 'Unpublish'
						o.btn_color = 'purple'
						o.new_status = {key: 'under_review', text: 'Unpublished'}
					} else {
						o.visible = false
					}
				}

			// IF DIRECTION IS 'next' (FORWARD IN THE PUBLISHING PROCESS)
			} else {
				o.key = 'next'
				o.icon = 'fa-right'
				// IF CURRENT STEP IS 'unsubmitted'
				if (key == 'unsubmitted') {
					// if the user is the author, clicking the next button submits for publication
					if (this.publish_roles.includes('author')) {
						o.btn_text = 'Submit for publication'
						o.btn_color = 'pink'
						
						if (!this.submit.enabled) {
							o.new_status = 'unclickable'
							if (this.submit.editor_open) {
								o.msg = `Close the lesson editor to enable submission`
							} else {
								o.msg = !this.submit.alignments ? `Align ${this.asset_type_noun} to standards` : ''
								if (!this.submit.review) {
									o.msg += o.msg.length ? ` and complete the ${this.site_config.asset_manager.text.review_required} below` : `Complete the ${this.site_config.asset_manager.text.review_required} below`
								}
								o.msg += ` to enable submission`
							}
							o.tooltip = o.msg
							o.class = 'k-asset-publish-current-unclickable k-disabled'
							o.btn_color = 'grey'
						} else {
							o.visible = true
							o.new_status = {key: 'submitted', text: 'Submitted for Publication'}
							o.msg = `Click the <i class="fas fa-right"></i> button above to submit this ${this.asset_type_noun} for publication`
							o.tooltip = 'Submit for<br>publication'
							o.class = ''
						}
					// if the user is an active reviewer or publisher, clicking the next button puts into review mode
					} else if (this.reviewer_or_publisher) {
						o.tooltip = 'Review for<br>publication'
						o.btn_text = 'Review for publication'
						o.btn_color = 'purple'
						o.new_status = {key: 'under_review', text: 'Under Review'}
						o.msg = `Click the <i class="fas fa-right"></i> button above to start the process of reviewing this ${this.asset_type_noun} for publication`

					} else {
						// I don't think we can get to here, but if we do, button is not visible
						o.visible = false
					}
				// IF CURRENT STEP IS 'submitted'
				} else if (key == 'submitted') {
					// for active reviewer/publisher, clicking the next button puts into review mode
					if (this.reviewer_or_publisher) {
						o.tooltip = 'Begin Review'
						o.btn_text = 'Begin Review'
						o.btn_color = 'purple'
						o.new_status = {key: 'under_review', text: 'Under Review'}
						o.msg = `Click the <i class="fas fa-right"></i> button above to begin reviewing this ${this.asset_type_noun} for publication`
					} else {
						// else next is not clickable
						o.visible = false
						o.btn_color = 'pink'
						o.msg = `This ${this.asset_type_noun} has been submitted for publication`
					}
				// IF CURRENT STEP IS 'under_review'
				} else if (key == 'under_review') {
					// for reviewer/publisher, clicking the next button moves to ready_for_publication
					if (this.user_is_admin || this.active_reviewer) {
						o.tooltip = 'Approve for<br>publication'
						o.btn_text = 'Approve for publication'
						o.btn_color = 'blue'
						o.new_status = {key: 'ready_for_publication', text: 'Approved for Publication'}
						o.msg = `Click the <i class="fas fa-right"></i> button above to approve this ${this.asset_type_noun} for publication`
					} else {
						// else next is not clickable
						o.visible = false
						o.btn_color = 'purple'
						o.msg = `This ${this.asset_type_noun} is currently under review.`
						
						if (this.assigned_reviewer) {
							o.msg += `<br>You are not currently an active reviewer.`

							if (this.assigned_reviewer.approved) {
								o.msg += `<br>You have approved this ${this.asset_type_noun}.`
							}
						}
					}
				// IF CURRENT STEP IS 'ready_for_publication'
				} else if (key == 'ready_for_publication') {
					// for publisher (NOT reviewer), clicking the next button publishes the item
					if (this.publish_roles.includes('publisher')) {
						o.tooltip = 'Publish!'
						o.btn_text = 'Publish'
						o.btn_color = 'green'
						o.new_status = {key: 'published', text: 'Published'}
						o.msg = `Click the <i class="fas fa-right"></i> button above to publish this ${this.asset_type_noun}`
					} else {
						// else next is not clickable
						o.visible = false
						o.btn_color = 'blue'
						o.msg = `This ${this.asset_type_noun} is currently approved for publication, but has not yet been published`
					}
				// IF CURRENT STEP IS 'published'
				} else if (key == 'published') {
					o.visible = false
					o.btn_color = 'green'
					o.msg = `This ${this.asset_type_noun} is published`
					if (this.publish_roles.includes('publisher')) {
						o.msg += ` Click the <i class="fas fa-left"></i> button above to unpublish it`
					}
				}
			}

			return o
		},

		async step_clicked(new_status) {
			// if new_status is 'unclickable', do nothing
			if (new_status == 'unclickable') return

			// if new_status.confirm is non-empty, we'll ask the user to confirm before taking the action
			if (!empty(new_status.confirm)) {
				try {
					await this.$confirm({
						title: 'Are you sure?',
						text: new_status.confirm,
					})
				} catch(e) { return }
			}

			if (new_status.key == 'ready_for_publication') {
				this.reviewer_approved(new_status)
			} else {
				this.set_publish_status(new_status)
			}
		},

		reviewer_approved(new_status) {
			if (this.active_reviewer) {
				let log_data = object_copy(this.active_reviewer)
				log_data.approved = 1
				log_data.active = 0
				log_data.approved_date = new Date().toJSON().slice(0, 19).replace('T', ' ')

				this.save_log(log_data)
					.then(result => {
						if (!result.status == 'ok') {
							console.error('something went wrong: ', result)
						} else {
							let all_approved = true
							this.local_item.reviewers = this.local_item.reviewers.map(x => {
								if (x.id == result.asset_log_entry.id) {
									x = result.asset_log_entry
								}
								if (!x.approved) all_approved = false
								return x
							})

							if (all_approved) {
								this.set_publish_status(new_status)
							} else {
								let status_log_data = {
									user_id: this.user_info.user_id,
									asset_id: log_data.asset_id,
									asset_type: log_data.asset_type,
									asset_creator_id: log_data.asset_creator_id,
									action: 'status',
									from: this.log_status_key(),
									to: new_status.key
								}

								this.save_log(status_log_data)
									.then(() => {
										this.$inform({text: 'Your approval has been recorded.<br>Status will be updated when all assigned reviewers have approved.'})
									})
							}
						}
					})
			} else {
				/** The only way to get here is if the user is an admin and can bypass all other checks */
				this.set_publish_status(new_status)
			}
		},

		async set_publish_status(new_status) {
			let today = new Date().toJSON().slice(0, 19).replace('T', ' ')
			let asset_data = object_copy(this.local_item)
			let payload = {}
			let asset_type = this.asset_type == 'lesson' ? 'lesson' : 'resource'
			let asset_id
			let asset_creator_id

			asset_data.publish_status = new_status.key
			asset_data.status_date = today

			if (this.site_config.new_indicator?.enabled && new_status.key === 'published' && !asset_data.new_until_date) {
				let default_date = U.calculate_new_until_date(this.site_config.new_indicator)
				await this.$prompt({
					title: 'Set "New" Indicator?',
					text: 'This asset can be marked as "New" until a set date. Enter a date below, or cancel to use the default.',
					promptType: 'text',
					type: 'date',
					initialValue: default_date,
					acceptText: 'Set and Publish',
					cancelText: 'Publish Without',
				}).then(date => {
					if (date) asset_data.new_until_date = date
				}).catch(() => { /* publish without, backend will auto-set */ })
			}
			
			switch (new_status.key) {
				case 'published':
					asset_data.published_date = today
					asset_data.agency_sanctioned = true

					if (this.payments_enabled) this.create_payment_record(today)
					break
				case 'submitted':
					asset_data.submitted_date = today
					//fallthrough intentional
				default:
					asset_data.agency_sanctioned = false
			}

			if (asset_type == 'lesson') {
				asset_id = asset_data.lesson_id
				asset_creator_id = asset_data.creator_user_id
				payload.lesson_class = asset_data.lesson_id === asset_data.template_id ? 'template' : 'teacher'
				payload.lesson_data = asset_data
			} else {
				asset_data = new Resource(asset_data)
				asset_id = asset_data.resource_id
				asset_creator_id = asset_data.creator
				payload.resource = asset_data
			}

			await this.$store.dispatch('save_' + asset_type, payload)
				.then(() => {
					let log_data = {
						user_id: this.user_info.user_id,
						asset_id,
						asset_type,
						asset_creator_id,
						action: 'status',
						from: this.log_status_key(),
						to: new_status.key
					}

					this.save_log(log_data)
						.then(() => {
							this.local_item.publish_status =  asset_data.publish_status
							this.local_item.agency_sanctioned =  asset_data.agency_sanctioned
							this.local_item.status_date = today
							this.item.publish_status =  asset_data.publish_status
							this.item.agency_sanctioned =  asset_data.agency_sanctioned
							this.item.status_date = today
							this.set_current_publish_status(new_status.key)
							if (asset_data.submitted_date) {
								this.item.submitted_date = asset_data.submitted_date
								this.local_item.submitted_date = asset_data.submitted_date
							}
							if (asset_data.new_until_date) {
								this.item.new_until_date = asset_data.new_until_date
								this.local_item.new_until_date = asset_data.new_until_date
							}
							this.load_log()
							this.$inform({text:'Status updated to: ' + new_status.text})
							this.$emit('status_updated', this.item, this.asset_type)
						})
				})
		},

		set_current_publish_status(publish_status) {
			this.current_publish_status = this.$get_publish_status_data(publish_status)
		},

		// current_publish_status.key is 'unsubmitted' for unpublished/rejected assets, so log the actual stored status instead
		log_status_key() {
			return this.$is_off_workflow_status(this.local_item.publish_status) ? this.local_item.publish_status : this.current_publish_status.key
		},

		review_saved(updated_review) {
			// if/when the review is saved, update it in this.asset_review.
			// note that the review is saved separately (in a different DB table) than the resource/lesson
			this.asset_review_data = updated_review
		},

		save_log(log_data) {
			let log_payload = {
				user_id: this.user_info.user_id,
				log_data
			}
			
			return this.$store.dispatch('save_asset_log', log_payload)
		},

		async load_log() {
			let payload = {
				asset_id: this.asset_id, 
				user_id: this.user_info.user_id
			}

			await U.ajax('get_asset_log', payload, result => {
				if (!result.status == 'ok') {
					console.error('something went wrong: ', result)
				} else {
					// sort with latest entries first
					this.log = result.log.sort((a,b) => {
						if (a.created_at < b.created_at) return 1
						if (a.created_at > b.created_at) return -1
						return 0
					})
				}
			})
		},

		mail_to() {
			let recipient = this.creator_email
			let subject = `RE: ${this.asset_type == 'lesson' ? this.item.lesson_title : this.item.description}`
			let mailtoLink = `mailto:${recipient}?subject=${encodeURIComponent(subject)}`

			window.open(mailtoLink, "_blank")
		},

		save_note() {
			this.local_item.review_note = this.note

			let payload
			let service

			if (this.asset_type === 'lesson') {
				let lesson_data = object_copy(this.local_item)
				let lesson_class = lesson_data.lesson_id === lesson_data.template_id ? 'template' : 'teacher'
				
				payload = {
					lesson_data,
					lesson_class
				}
				service = 'save_lesson'
			} else {
				payload = {resource: new Resource(object_copy(this.local_item))}
				service = 'save_resource'
			}

			this.$store.dispatch(service, payload)
				.then(() => {
					this.$inform({text: 'Note Updated'})
				})
		},

		copy_email_to_clipboard() {
			navigator.clipboard.writeText(this.creator_email)
			.then(() => {
					this.$inform('Email address copied to clipboard')
				}
			)
		},

		create_payment_record(publish_date) {
			let payment_data

			if (!this.payment_data) {		
				let is_lesson = this.asset_type == 'lesson'
				
				let asset_type = is_lesson ? 'lesson' : this.local_item.type == 'sparkl' ? 'activity' : 'resource'
				let asset_id = is_lesson ? this.local_item.lesson_id : this.local_item.resource_id
				let asset_title = is_lesson ? this.local_item.lesson_title : this.local_item.description
				let author_id = is_lesson ? this.local_item.creator_user_id : this.local_item.creator
				if (!publish_date) publish_date = '*CLEAR*'

				payment_data = {
					asset_id,
					asset_title,
					asset_type,
					author_id,
					submitted_date: this.local_item.submitted_date || '*CLEAR*',
					author_name: this.creator_name,
					publish_date,
					reviewer_data: []
				}

				this.local_item.reviewers.forEach(reviewer => {
					let approved_date = null

					if (reviewer.approved) approved_date = this.get_approval_date(reviewer)

					let reviewer_data = {
						user_id: reviewer.assigned_user_id,
						user_name: reviewer.assigned_user_name,
						approved_date,
						payments: []
					}

					payment_data.reviewer_data.push(reviewer_data)
				})
			} else {
				payment_data = object_copy(this.payment_data)
				if (publish_date) payment_data.publish_date = publish_date
			}

			return this.$store.dispatch('save_asset_review_payment', {user_id: this.user_info.user_id, payment_data})
		},

		payment_updated(payment_data) {
			if (!payment_data.publish_date) payment_data.publish_date = '*CLEAR*'
			this.$store.dispatch('save_asset_review_payment', {user_id: this.user_info.user_id, payment_data})
				.then(() => {
					this.payment_data = payment_data
				})
		},

		get_approval_date(reviewer_data) {
			let approval = this.log.filter(x => {
				let user = x.user_id == reviewer_data.assigned_user_id
				let to = x.to == 'ready_for_publication'
				return user && to
			})
			
			return approval.length ? approval[0].created_at : null
		},

		// AssetReviewerDialog may revert publish_status on its own (e.g. adding a reviewer to an asset approved for publication);
		// current_publish_status is separate reactive state driving the step UI, so it needs to be re-synced here too
		reviewers_updated(updated_item) {
			this.local_item = updated_item
			this.set_current_publish_status(updated_item.publish_status)

			// keep the item prop in sync too, and notify ancestors (e.g. AssetManagerTable's row) the same way set_publish_status does
			this.item.publish_status = updated_item.publish_status
			this.item.status_date = updated_item.status_date
			this.item.agency_sanctioned = updated_item.agency_sanctioned
			this.item.reviewers = updated_item.reviewers
			this.$emit('status_updated', this.item, this.asset_type)
		},

		async manage_reviewers() {
			if (this.payment_data) {
				this.managing_reviewers = true
			} else {
				U.loading_start()
				let result = await this.load_payment_data()
				U.loading_stop()

				if (!result.status == 'ok') {
					console.error('something went wrong: ', result)
				} else if (result.payment_data) {
					this.payment_data = result.payment_data
				}
				this.managing_reviewers = true
			}
		},

		load_payment_data() {
			return new Promise((resolve) => {
						let payload = {
							user_id: this.user_info.user_id,
							asset_id: this.asset_id
						}

						U.ajax('get_asset_review_payment', payload, result => {
							resolve(result)
						})
					})
		},

		async manage_payments() {
			if (this.payment_data) {
				this.managing_payments = true
			} else {
				U.loading_start()
				let result = await this.load_payment_data()
				U.loading_stop()

				if (!result.status == 'ok') {
					console.error('something went wrong: ', result)
				} else if (result.payment_data) {
					this.payment_data = result.payment_data
					this.managing_payments = true
				} else {
					this.create_payment_record()
						.then(result => {
							this.payment_data = result.payment_data
							this.managing_payments = true
						})
				}
			}
		},
	}
}
</script>

<style lang="scss">
.k-asset-publish-status-outer {
	display: flex;
	padding: 8px;
	border-radius: 12px;
	background-color: #f1f1f1;
	box-shadow: 0 1px 3px 1px rgba(64, 64, 64, 0.5);
}

.k-asset-publish-msg {
	text-align: center;
	padding: 4px 8px;
	border-radius: 8px;
}

.k-asset-publish-step {
	font-size: 14px;
	line-height: 16px;
	text-align: center;
	flex: 0 0 108px;
	height: 108px;
	width: 108px;
	border: 1px solid #000;
	border-radius: 108px;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	cursor: pointer;

	.v-icon {
		margin-bottom: 4px;
		margin-top: -12px;
		font-size: 24px;
	}
}

.k-asset-publish-step-placeholder {
	text-align: center;
	flex: 0 0 108px;
}

.k-asset-publish-line {
	flex: 0 0 24px;
	width: 24px;
	border-bottom: 4px solid #000;
}

.k-asset-publish-line-placeholder {
	flex: 0 0 24px;
}

.k-asset-publish-earlier-step {
	color: #ccc;
	.v-icon { color: #ccc; }
}
.k-asset-publish-current-step {
	color: #fff;
	font-weight: bold;
	border-width: 4px;
	.v-icon { color: #fff; }
}
.k-asset-publish-later-step {
	color: #000;
	.v-icon { color: #666; }
}

.k-asset-publish-current-unclickable {
	cursor: default;

	&.k-disabled {
		opacity: .5;
	}
}

.k-asset-publish-step-unsubmitted { background-color: $v-deep-orange-darken-3; }
.k-asset-publish-step-unsubmitted.k-asset-publish-later-step { background-color: $v-deep-orange-lighten-4; }

.k-asset-publish-step-submitted { background-color: $v-pink-darken-3; }
.k-asset-publish-step-submitted.k-asset-publish-later-step { background-color: $v-pink-lighten-4; }

.k-asset-publish-step-under_review { background-color: $v-purple-darken-3; }
.k-asset-publish-step-under_review.k-asset-publish-later-step { background-color: $v-purple-lighten-4; }

.k-asset-publish-step-ready_for_publication { background-color: $v-blue-darken-3; }
.k-asset-publish-step-ready_for_publication.k-asset-publish-later-step { background-color: $v-blue-lighten-4; }

.k-asset-publish-step-published { background-color: $v-green-darken-3; }
.k-asset-publish-step-published.k-asset-publish-later-step { background-color: $v-green-lighten-4; }

.k-asset-publish-status-card-people {
	text-align: center;
	font-size: 16px;
	line-height: 1.3em;

	.k-user-info {
		width: 180px;
		margin: 0 auto;

		.k-active-icon {
			width: 20px;
		}
	}
}
</style>
